import { createContext, FC, PropsWithChildren, useCallback, useContext, useRef } from "react";

import { MapsTokenResponse } from "@asd/customer-portal-api";
import { TokenCredential } from "@azure/core-auth";
import MapsSearch, { MapsSearchClient } from "@azure-rest/maps-search";

import { MapsService } from "../services";
import { expandAbbreviations } from "../utils/mapProviderHelpers";

type MapsContextType = {
  getMapToken: () => Promise<MapsTokenResponse>;
  queryForAddress: (_address: string) => Promise<any>;
  getCoordinates: (_address: string) => Promise<any>;
};

const MapsContext = createContext<MapsContextType>({} as MapsContextType);

export const useMapsContext = () => useContext(MapsContext);

const MapsProvider: FC<PropsWithChildren> = ({ children }) => {
  const tokenRef = useRef<MapsTokenResponse | null>(null);
  const mapClient = useRef<MapsSearchClient>(null);

  const getMapToken = useCallback(async () => {
    if (tokenRef.current && new Date(tokenRef.current.expiresAt!).getTime() > Date.now()) {
      return tokenRef.current;
    }

    const mapTokenResponse = await MapsService.getMapToken();

    if (mapTokenResponse?.status !== 200) {
      throw new Error("Failed to retrieve map token");
    }

    tokenRef.current = mapTokenResponse.data;
    return mapTokenResponse.data;
  }, []);

  const getClient = useCallback(async () => {
    if (!mapClient.current) {
      const token = await getMapToken();
      const tokenCredential: TokenCredential = {
        getToken: async () => {
          const refreshToken = await getMapToken();
          return {
            token: refreshToken.accessToken ?? "",
            expiresOnTimestamp: new Date(refreshToken.expiresAt!).getTime(),
          };
        },
      };
      mapClient.current = MapsSearch(tokenCredential, token.clientId!);
    }
    return mapClient.current;
  }, [getMapToken]);

  const queryForAddress = useCallback(
    async (address: string) => {
      const client = await getClient();

      const normalized = expandAbbreviations(address.toLowerCase()).replace(/,+/g, " ").replace(/\s+/g, " ").trim();

      const withoutNumber = normalized.replace(/^\d+\s+/, "").trim();
      const hasNumber = withoutNumber !== normalized;

      const queries = [
        (client as any).path("/geocode").get({
          queryParameters: {
            "api-version": "2026-01-01",
            query: normalized,
            top: 5,
          },
        }),
        (client as any).path("/geocode:autocomplete").get({
          queryParameters: {
            "api-version": "2026-01-01",
            query: normalized,
            top: 5,
            countryRegion: "US",
            coordinates: [-77.3861, 38.9696],
          },
        }),
        ...(hasNumber
          ? [
              (client as any).path("/geocode:autocomplete").get({
                queryParameters: {
                  "api-version": "2026-01-01",
                  query: withoutNumber,
                  top: 5,
                  countryRegion: "US",
                  coordinates: [-77.3861, 38.9696],
                },
              }),
            ]
          : []),
      ];

      const results = await Promise.allSettled(queries);

      const features: any[] = [];
      const seen = new Set<string>();

      const addFeatures = (list: any[]) => {
        for (const f of list) {
          const key = f.properties?.address?.formattedAddress ?? f.properties?.name;
          if (key && !seen.has(key)) {
            seen.add(key);
            features.push(f);
          }
        }
      };

      for (const result of results) {
        if (result.status === "fulfilled") {
          const status = Number(result.value.status);
          if (status === 200) {
            const body = typeof result.value.body === "string" ? JSON.parse(result.value.body) : result.value.body;
            addFeatures(body.features ?? []);
          } else if (status !== 400 && status !== 404) {
            throw new Error(`Unexpected status from maps API: ${status}`);
          }
        } else {
          throw new Error("Maps API request failed:", result.reason);
        }
      }

      return { features };
    },
    [getClient],
  );

  const getCoordinates = useCallback(
    async (address: string) => {
      const client = await getClient();
      const response = await (client as any).path("/geocode").get({
        queryParameters: {
          "api-version": "2025-06-01-preview",
          query: address,
        },
      });

      if (response.status !== "200") {
        throw new Error("Failed to retrieve coordinates");
      }

      return response.body;
    },
    [getClient],
  );

  return <MapsContext.Provider value={{ getMapToken, queryForAddress, getCoordinates }}>{children}</MapsContext.Provider>;
};

export default MapsProvider;
